<template>
  <div>
    <share-template-instructions-alert :template="template">
    </share-template-instructions-alert>
    <v-card class="mb-4">
      <v-card-title>
        Which list do you want to send this email to?
      </v-card-title>
      <v-card-text>
        <v-autocomplete v-if="lists.length > 0"
                        v-model="list"
                        label="Search for an existing list"
                        outlined
                        placeholder="Search for an existing list"
                        :items="lists"
                        item-value="id"
                        item-text="name"
                        return-object
                        @change="selectList">
          <template v-slot:item="{parent, item}">
            <v-list-item-content>
              <v-list-item-title>
                {{ item.name }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ item.subscriber_count | number }} active {{ item.subscriber_count | pluralize('subscriber', false) }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>

        <div v-if="lists.length > 0">
          Or <a href="#create-list" @click.prevent="createList">create a new list</a> and add contacts to it now.
        </div>
        <div v-else>
          <a href="#create-list" @click.prevent="createList">create a new list</a> and add contacts to it now.
        </div>
        <div id="create-list"></div>
      </v-card-text>
      <v-card-actions class="px-4">
        <v-btn @click.prevent="goBack"
               :disabled="loading">
          Go back
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn @click.prevent="submit"
               :disabled="loading || !form.list_id || (list && list.subscriber_count === 0)"
               color="primary">
          Next
        </v-btn>
      </v-card-actions>
    </v-card>

    <template v-if="list">
      <v-form @submit.prevent=""
              ref="addContactForm">
        <v-card class="mb-4">
          <v-card-title>
            <div>
              Add contacts to this list
            </div>
          </v-card-title>
          <v-card-text>
            <search-for-user-contact @selected="addContact">
            </search-for-user-contact>
          </v-card-text>
        </v-card>
      </v-form>

      <v-card>
        <subscribers-table :list="list"
                           :loading="loading"
                           :search="search"
                           :subscribers="subscribers"
                           @updated="fetchData">
        </subscribers-table>
      </v-card>
    </template>
  </div>
</template>
<script>
import {mapGetters} from 'vuex';
import {Form} from 'vform';
import {isObject, orderBy} from 'lodash';
import mailcoachService from '../../../services/mailcoachService';
import SubscribersTable from './components/SubscribersTable';
import ShareTemplateInstructionsAlert from "./components/ShareTemplateInstructionsAlert";
import SearchForUserContact from '../components/SearchForUserContact.vue';
import moment from 'moment-timezone';
import DOMPurify from 'dompurify';

export default {
  name: 'email-campaign-recipients',
  components: {SearchForUserContact, ShareTemplateInstructionsAlert, SubscribersTable},
  data() {
    return {
      form: new Form({
        list_id: null,
        list_name: null,
      }),
      list: null,
      lists: [],
      loading: false,
      search: null,
      subscribers: [],
    }
  },
  computed: {
    ...mapGetters('emailCampaignCart', [
      'cart',
      'template',
    ]),
    listID() {
      if (this.$route.query.list_id) {
        return parseInt(this.$route.query.list_id);
      }
      return null;
    }
  },
  created() {
    if (!this.template) {
      this.$router.replace({name: `${this.$root.routePrefix}.mailcoach.campaign.wizard.template`, query: this.$route.query});
      return;
    }
    this.fetchData();
  },
  methods: {
    fetchData() {
      if (this.loading) {
        return;
      }
      this.loading = true;
      return Promise.all([
        this.getLists(),
      ])
        .then(async () => {
          if (this.cart.list) {
            this.form.list_id = this.cart.list.id;
          } else if (this.listID) {
            this.form.list_id = this.listID;
          }
          if (this.form.list_id) {
            this.list = await mailcoachService.getList(this.form.list_id);
          }
          await this.selectList();
        })
        .finally(() => {
          this.loading = false;
          if (this.lists.length === 0) {
            this.$router.replace({name: `${this.$root.routePrefix}.mailcoach.campaigns`});
          }
        })
    },
    async getLists() {
      return mailcoachService.getLists()
        .then(lists => {
          this.lists = orderBy(lists, ['updated_at'], ['desc']);
        })
    },
    async selectList() {
      if (!this.list) {
        return;
      }
      this.form.list_id = this.list.id;
      if (!this.form.list_id) {
        return;
      }
      this.loading = true;
      this.subscribers = [];
      this.list = await mailcoachService.getList(this.form.list_id);
      return mailcoachService.getActiveListSubscribers(this.form.list_id)
        .then(data => {
          this.subscribers = orderBy(data, ['contact.email', 'contact.last_name', 'contact.first_name'], ['asc', 'asc', 'asc']);
        })
        .finally(() => {
          this.loading = false;
        })
    },
    goBack() {
      this.$store.dispatch('emailCampaignCart/prevStep');
      this.$router.replace({name: `${this.$root.routePrefix}.mailcoach.campaign.wizard.template`, query: this.$route.query});
    },
    submit() {
      this.$store.dispatch('emailCampaignCart/setList', this.list);
      this.$store.dispatch('emailCampaignCart/nextStep');
      this.$router.push({name: `${this.$root.routePrefix}.mailcoach.campaign.wizard.review`, query: this.$route.query});
    },
    async createList() { // MLD-4958
      try {
        const form = {
          name: `${moment().format('YYYY-MM-DD HH:mm:ss')} List`
        }
        this.list = await mailcoachService.createList(form);
        this.form.list_id = this.list.id;
        await this.getLists();
        await this.selectList();
        this.$toast.success(`New list created. You can add contacts to it.`);
      } catch (e) {
        console.log(e.message, e);
      }
    },
    async addContact(contact) {
      if (!this.list) {
        return;
      }
      if (isObject(contact) && contact.id) {
        mailcoachService.addContacts(this.list.id, [contact.id])
          .then(data => {
            this.$refs.addContactForm.reset();
            this.$toast.success(`Added ${DOMPurify.sanitize(contact.email)} to this list.`);
            this.getLists();
            this.selectList();
          })
      }
    }
  }
}
</script>
